import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Package, CheckCircle, Truck } from 'lucide-react';
import { toast } from 'sonner';

const OrderTracking = ({ user, onLogout }) => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      toast.error('Error al cargar pedido');
    } finally {
      setLoading(false);
    }
  };

  const getStatusSteps = () => {
    return [
      { status: 'CREATED', label: 'Creado', icon: Package },
      { status: 'READY_FOR_PICKUP', label: 'Listo', icon: Clock },
      { status: 'IN_CUSTODY', label: 'En Custodia', icon: Truck },
      { status: 'EN_ROUTE', label: 'En Ruta', icon: Truck },
      { status: 'DELIVERED', label: 'Entregado', icon: CheckCircle }
    ];
  };

  const getCurrentStepIndex = (status) => {
    const steps = getStatusSteps();
    return steps.findIndex(s => s.status === status);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} title="Tracking">
        <div className="text-center py-12">
          <div className="text-lg" style={{ color: '#224049' }}>Cargando...</div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout user={user} onLogout={onLogout} title="Tracking">
        <div className="text-center py-12">
          <div className="text-lg" style={{ color: '#D32F2F' }}>Pedido no encontrado</div>
        </div>
      </Layout>
    );
  }

  const currentStepIndex = getCurrentStepIndex(order.status);
  const steps = getStatusSteps();

  return (
    <Layout user={user} onLogout={onLogout} title="Tracking de Pedido">
      <div className="max-w-4xl mx-auto space-y-6" data-testid="order-tracking-container">
        {/* Order Header */}
        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl" style={{ color: '#224049' }}>
                  Pedido {order.id.substring(0, 8)}
                </CardTitle>
                <p className="text-sm" style={{ color: '#666' }}>{order.customer.name}</p>
              </div>
              <span className={order.mode === 'EXPRESS' ? 'mode-express' : 'mode-standard'}>
                {order.mode === 'EXPRESS' ? 'EXPRESS 60\'' : 'STANDARD'}
              </span>
            </div>
          </CardHeader>
        </Card>

        {/* Progress Tracker */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle style={{ color: '#224049' }}>Estado del Pedido</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <div className="flex justify-between items-center">
                {steps.map((step, index) => {
                  const StepIcon = step.icon;
                  const isActive = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;

                  return (
                    <div key={step.status} className="flex flex-col items-center flex-1" data-testid={`step-${index}`}>
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
                        style={{
                          backgroundColor: isActive ? '#224049' : '#F0EBE3',
                          border: isCurrent ? '3px solid #D4AD6A' : 'none'
                        }}
                      >
                        <StepIcon className="w-6 h-6" style={{ color: isActive ? '#D4AD6A' : '#666' }} />
                      </div>
                      <p className="text-xs text-center font-medium" style={{ color: isActive ? '#224049' : '#999' }}>
                        {step.label}
                      </p>
                      {index < steps.length - 1 && (
                        <div
                          className="absolute top-6 h-1"
                          style={{
                            left: `${(index / (steps.length - 1)) * 100 + 10}%`,
                            width: `${80 / (steps.length - 1)}%`,
                            backgroundColor: isActive ? '#224049' : '#F0EBE3'
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Order Details */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle style={{ color: '#224049' }}>Detalles de Entrega</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium" style={{ color: '#666' }}>Dirección</p>
                <p style={{ color: '#224049' }}>
                  {order.delivery_address.line1}<br />
                  {order.delivery_address.city}, {order.delivery_address.state} {order.delivery_address.postal_code}
                </p>
                {order.delivery_address.notes && (
                  <p className="text-sm mt-1" style={{ color: '#666' }}>{order.delivery_address.notes}</p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: '#666' }}>Teléfono</p>
                <p style={{ color: '#224049' }}>{order.customer.phone}</p>
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: '#666' }}>PIN de Entrega</p>
                <p className="text-3xl font-bold tracking-wider" style={{ color: '#D4AD6A' }}>{order.pin}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle style={{ color: '#224049' }}>Artículos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center p-2 rounded" style={{ backgroundColor: '#EAE0D5' }}>
                  <div>
                    <p className="font-medium" style={{ color: '#224049' }}>{item.name}</p>
                    <p className="text-sm" style={{ color: '#666' }}>SKU: {item.sku}</p>
                  </div>
                  <p className="font-semibold" style={{ color: '#D9A27F' }}>x{item.quantity}</p>
                </div>
              ))}
              <div className="pt-3 border-t flex justify-between items-center">
                <p className="font-semibold" style={{ color: '#224049' }}>Tarifa de Envío</p>
                <p className="text-xl font-bold" style={{ color: '#D9A27F' }}>${order.delivery_fee.toFixed(2)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map placeholder */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle style={{ color: '#224049' }}>Mapa de Tracking</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="w-full h-64 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#F0EBE3' }}
            >
              <div className="text-center">
                <MapPin className="w-16 h-16 mx-auto mb-2" style={{ color: '#D9A27F' }} />
                <p style={{ color: '#224049' }}>Mapa en tiempo real (próximamente)</p>
                <p className="text-sm" style={{ color: '#666' }}>
                  Lat: {order.delivery_address.lat}, Lng: {order.delivery_address.lng}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Refund Warning */}
        {order.refund_eligible && (
          <Card className="shadow-lg" style={{ borderLeft: '4px solid #F57C00' }}>
            <CardContent className="py-4">
              <p className="font-semibold" style={{ color: '#F57C00' }}>
                ⚠️ Este pedido es elegible para reembolso debido al incumplimiento del SLA Express
              </p>
            </CardContent>
          </Card>
        )}

        <Button
          onClick={() => window.location.href = '/orders'}
          variant="outline"
          className="w-full"
          data-testid="back-to-orders-button"
        >
          Volver a Pedidos
        </Button>
      </div>
    </Layout>
  );
};

export default OrderTracking;